import { useState } from 'react';
import { useAuth } from '../contexts/auth-context';
import WatchedModal from './watched-modal';
import ShareModal from './share-modal';
import { trackContentInteraction } from '../utils/gtm';
import Icon from './icon';
import { UI_LABELS, TOOLTIP_MESSAGES, CONSOLE_MESSAGES } from '../constants';
import { SPINNER_CLASSES, BUTTON_CLASSES, cn } from '../styles';

function MediaActionButtons({ item, mediaType, onRequireAuth }) {
  const [isLoading, setIsLoading] = useState({ favorites: false, watchlist: false, watched: false });
  const [showWatchedModal, setShowWatchedModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const { 
    addToFavorites, 
    removeFromFavorites, 
    addToWatchlist, 
    removeFromWatchlist,
    isInFavorites,
    isInWatchlist,
    isWatched,
    isAuthenticated 
  } = useAuth();

  // For non-authenticated users, show default states
  const isFavorite = isAuthenticated ? isInFavorites(item.id, mediaType) : false;
  const inWatchlist = isAuthenticated ? isInWatchlist(item.id, mediaType) : false;
  const watched = isAuthenticated ? isWatched(item.id, mediaType) : false;

  const handleFavoriteToggle = async () => {
    if (!isAuthenticated) {
      onRequireAuth?.();
      return;
    }
    
    setIsLoading(prev => ({ ...prev, favorites: true }));
    try {
      if (isFavorite) {
        await removeFromFavorites(item.id, mediaType);
        // Track remove from favorites
        trackContentInteraction('remove_from_favorites', mediaType, {
          id: item.id,
          title: item.title || item.name,
          rating: item.vote_average || 0,
        });
      } else {
        await addToFavorites(item.id, mediaType);
        // Track add to favorites
        trackContentInteraction('add_to_favorites', mediaType, {
          id: item.id,
          title: item.title || item.name,
          rating: item.vote_average || 0,
        });
      }
    } catch (error) {
      console.error(CONSOLE_MESSAGES.ERROR_TOGGLING_FAVORITE, error);
    } finally {
      setIsLoading(prev => ({ ...prev, favorites: false }));
    }
  };

  const handleWatchlistToggle = async () => {
    if (!isAuthenticated) {
      onRequireAuth?.();
      return;
    }
    
    setIsLoading(prev => ({ ...prev, watchlist: true }));
    try {
      if (inWatchlist) {
        await removeFromWatchlist(item.id, mediaType);
        // Track remove from watchlist
        trackContentInteraction('remove_from_watchlist', mediaType, {
          id: item.id,
          title: item.title || item.name,
          rating: item.vote_average || 0,
        });
      } else {
        await addToWatchlist(item.id, mediaType);
        // Track add to watchlist
        trackContentInteraction('add_to_watchlist', mediaType, {
          id: item.id,
          title: item.title || item.name,
          rating: item.vote_average || 0,
        });
      }
    } catch (error) {
      console.error(CONSOLE_MESSAGES.ERROR_TOGGLING_WATCHLIST, error);
    } finally {
      setIsLoading(prev => ({ ...prev, watchlist: false }));
    }
  };

  const handleWatchedToggle = async () => {
    if (!isAuthenticated) {
      onRequireAuth?.();
      return;
    }
    
    // Track watched modal open
    trackContentInteraction('open_watched_modal', mediaType, {
      id: item.id,
      title: item.title || item.name,
      rating: item.vote_average || 0,
    });
    
    // Always open modal for watched items to allow editing
    setShowWatchedModal(true);
  };

  const handleShareToggle = () => {
    // Track share modal open
    trackContentInteraction('open_share_modal', mediaType, {
      id: item.id,
      title: item.title || item.name,
      rating: item.vote_average || 0,
    });
    
    setShowShareModal(true);
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
        {/* Favorites Button */}
        <button
          onClick={handleFavoriteToggle}
          disabled={isLoading.favorites}
          className={cn(
            'flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all duration-200 flex-1 sm:flex-initial',
            isFavorite
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-blue-500 hover:bg-blue-600 text-white',
            BUTTON_CLASSES.DISABLED
          )}
          title={isFavorite ? TOOLTIP_MESSAGES.REMOVE_FROM_FAVORITES : TOOLTIP_MESSAGES.ADD_TO_FAVORITES}
        >
          {isLoading.favorites ? (
            <div className={SPINNER_CLASSES.MEDIUM_CURRENT}></div>
          ) : (
            <Icon name={isFavorite ? "heart-filled" : "heart"} className="w-4 h-4 flex-shrink-0" />
          )}
          <span className="text-sm sm:text-base">
            {isFavorite ? UI_LABELS.FAVORITE : UI_LABELS.ADD_TO_FAVORITES}
          </span>
        </button>

        {/* Watchlist Button */}
        <button
          onClick={handleWatchlistToggle}
          disabled={isLoading.watchlist}
          className={cn(
            'flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all duration-200 flex-1 sm:flex-initial',
            inWatchlist
              ? 'bg-primary-600 hover:bg-primary-700 text-white'
              : 'bg-dark-700 hover:bg-dark-600 text-gray-300 hover:text-white border border-dark-600',
            BUTTON_CLASSES.DISABLED
          )}
          title={inWatchlist ? TOOLTIP_MESSAGES.REMOVE_FROM_WATCHLIST : TOOLTIP_MESSAGES.ADD_TO_WATCHLIST}
        >
          {isLoading.watchlist ? (
            <div className={SPINNER_CLASSES.MEDIUM_CURRENT}></div>
          ) : (
            <Icon name="clipboard" className="w-4 h-4 flex-shrink-0" />
          )}
          <span className="text-sm sm:text-base">
            {inWatchlist ? UI_LABELS.IN_WATCHLIST : UI_LABELS.ADD_TO_WATCHLIST}
          </span>
        </button>

        {/* Watched Button */}
        <button
          onClick={handleWatchedToggle}
          disabled={isLoading.watched}
          className={cn(
            'flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all duration-200 flex-1 sm:flex-initial',
            watched
              ? 'bg-green-600 hover:bg-green-700 text-white'
              : 'bg-dark-700 hover:bg-dark-600 text-gray-300 hover:text-white border border-dark-600',
            BUTTON_CLASSES.DISABLED
          )}
          title={watched ? TOOLTIP_MESSAGES.ALREADY_WATCHED : TOOLTIP_MESSAGES.MARK_AS_WATCHED}
        >
          {isLoading.watched ? (
            <div className={SPINNER_CLASSES.MEDIUM_CURRENT}></div>
          ) : (
            <Icon name="eye" className="w-4 h-4 flex-shrink-0" />
          )}
          <span className="text-sm sm:text-base">
            {watched ? UI_LABELS.ALREADY_WATCHED : UI_LABELS.MARK_AS_WATCHED}
          </span>
        </button>

        {/* Share Button */}
        <button
          onClick={handleShareToggle}
          className={cn(
            'flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all duration-200 flex-1 sm:flex-initial',
            'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white'
          )}
          title={TOOLTIP_MESSAGES.SHARE}
        >
          <Icon name="share" className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm sm:text-base">
            {UI_LABELS.SHARE}
          </span>
        </button>
      </div>

      {/* Watched Modal - Only show if authenticated */}
      {isAuthenticated && (
        <WatchedModal
          isOpen={showWatchedModal}
          onClose={() => setShowWatchedModal(false)}
          item={item}
          mediaType={mediaType}
        />
      )}

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        item={item}
        mediaType={mediaType}
      />
    </>
  );
}

export default MediaActionButtons; 